import { Schedule } from "@mui/icons-material";
import { Box, Chip, Typography } from "@mui/material";
import { DateTime } from "luxon";
import { ReactElement } from "react";
import { FormattedDate, FormattedMessage } from "react-intl";

import { InboxType } from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import ThreadIcon from "~frontend/components/threads/ThreadIcon";
import { useIntl } from "~frontend/providers/IntlProvider";
import { palette } from "~frontend/theme";
import formatContactName from "~frontend/util/formatContactName";
import { prettyPhoneNumber } from "~frontend/util/phoneNumber";
import { useClock } from "~frontend/util/useClock";

import { AssignmentSummary, InboxDetails } from "./types";

const TEN_MINUTES_MILLISECONDS = 10 * 60 * 1000;
const ONE_MINUTE_MILLISECONDS = 60 * 1000;
const ONE_HOUR_MILLISECONDS = 60 * ONE_MINUTE_MILLISECONDS;
const ONE_DAY_MILLISECONDS = 24 * ONE_HOUR_MILLISECONDS;

export function NonCampaignAssignmentRow({
  assignment,
  loading,
  inbox,
}: {
  assignment: AssignmentSummary;
  inbox: InboxDetails;
  loading: boolean;
}): ReactElement {
  const intl = useIntl();
  const now = useClock();
  const noCampaignFound = intl.formatMessage({
    defaultMessage: "No associated campaign",
    description: "Label for a thread without an owning campaign",
  });
  const expiresSoon =
    DateTime.fromMillis(assignment.expiresAt).diff(now).toMillis() <
    TEN_MINUTES_MILLISECONDS;

  // Determine layout type based on content
  const useTwoColumnsLayout =
    expiresSoon || inbox.inboxType === InboxType.Escalation;

  const phoneNumber = (
    <PhoneNumber
      toPhoneNumber={assignment.toPhoneNumber}
      fromPhoneNumber={assignment.fromPhoneNumber}
    />
  );
  const lastMessageTime = (
    <LastMessageTime
      timestamp={assignment.lastMessageAt}
      expiresSoon={expiresSoon}
    />
  );
  const campaignName = (
    <Typography
      variant="body2"
      sx={{ marginTop: 1 }}
      data-testid="assignment-campaign-name-label"
    >
      {assignment.campaignName || noCampaignFound}
    </Typography>
  );
  const escalationLabel = inbox.inboxType === InboxType.Escalation && (
    <Chip
      sx={{
        height: "25px",
        borderRadius: 0,
        backgroundColor: palette.lightestOrange,
        marginTop: 1,
        "& .MuiChip-label": {
          paddingLeft: 0.5,
        },
      }}
      label={
        <>
          <Typography variant="h3Caps" component="span">
            <FormattedMessage
              defaultMessage="Escalation:"
              description="Prefix for the thread escalation label"
            />
          </Typography>
          <Typography variant="body2" component="span">
            {" "}
            {inbox.inboxName}
          </Typography>
        </>
      }
    />
  );

  // When the thread is not an escalation nor about to expire, the toFrom and
  // last message timestamp can fit into the same row and maintain centering.
  const leftColumnContent = (
    <FlexBox direction="column">
      <ContactName assignment={assignment} loading={loading} />
      <Box sx={{ marginLeft: "14px" }}>
        <Box sx={{ marginTop: 1 }}>
          {useTwoColumnsLayout ? (
            phoneNumber
          ) : (
            <FlexBox
              direction="row"
              justifyContent="space-between"
              sx={{ marginTop: 1 }}
            >
              {phoneNumber}
              {lastMessageTime}
            </FlexBox>
          )}
        </Box>
        {campaignName}
        {escalationLabel}
      </Box>
    </FlexBox>
  );

  // Choose layout based on content requirements
  return useTwoColumnsLayout ? (
    <FlexBox justifyContent="space-between" direction="row">
      {leftColumnContent}
      <FlexBox direction="column" justifyContent="center">
        {lastMessageTime}
      </FlexBox>
    </FlexBox>
  ) : (
    leftColumnContent
  );
}

export function CampaignAssignmentRow({
  assignment,
  loading,
}: {
  assignment: AssignmentSummary;
  loading: boolean;
}): ReactElement {
  const now = useClock();
  const expiresSoon =
    DateTime.fromMillis(assignment.expiresAt).diff(now).toMillis() <
    TEN_MINUTES_MILLISECONDS;
  return (
    <FlexBox
      justifyContent="space-between"
      alignItems="center"
      sx={{ paddingTop: 1, paddingBottom: 1 }}
    >
      <ContactName assignment={assignment} loading={loading} />
      {assignment.lastMessageAt && (
        <LastMessageTime
          timestamp={assignment.lastMessageAt}
          expiresSoon={expiresSoon}
        />
      )}
    </FlexBox>
  );
}

function LastMessageTime({
  timestamp,
  expiresSoon,
}: {
  timestamp: number;
  expiresSoon: boolean;
}): ReactElement {
  const now = useClock();

  const timeInMilli = DateTime.fromMillis(timestamp);
  const millisSinceLastMessage = now.diff(timeInMilli).toMillis();

  let timeStamp: ReactElement;

  if (millisSinceLastMessage < ONE_MINUTE_MILLISECONDS) {
    timeStamp = (
      <FormattedMessage
        defaultMessage="Now"
        description="Last message at label for assignments that have messages from the last minute"
      />
    );
  } else if (millisSinceLastMessage < ONE_HOUR_MILLISECONDS) {
    timeStamp = (
      <FormattedMessage
        defaultMessage="{minutes} min ago"
        description="Last message at label for assignments that have messages from the last hour"
        values={{ minutes: Math.floor(millisSinceLastMessage / (60 * 1000)) }}
      />
    );
  } else if (millisSinceLastMessage < ONE_DAY_MILLISECONDS) {
    timeStamp = (
      <FormattedMessage
        defaultMessage="{hours} hr ago"
        description="Last message at label for assignments that have messages from the last day"
        values={{
          hours: Math.floor(millisSinceLastMessage / (60 * 60 * 1000)),
        }}
      />
    );
  } else {
    timeStamp = (
      <FormattedDate
        value={timestamp}
        year="numeric"
        month="short"
        day="numeric"
      />
    );
  }

  return (
    <FlexBox alignItems="center" flexDirection="column">
      {expiresSoon && (
        <FlexBox
          alignItems="center"
          sx={{
            backgroundColor: palette.lightOrange,
            padding: "2px",
            marginBottom: "2px",
          }}
        >
          <Schedule sx={{ height: "14px" }} />
          <Typography variant="body2" component="span">
            <FormattedMessage
              defaultMessage="Expires soon"
              description={"Alert for an assignment that is expiring soon"}
            />
          </Typography>
        </FlexBox>
      )}
      <Typography variant="body2">{timeStamp}</Typography>
    </FlexBox>
  );
}

function PhoneNumber({
  toPhoneNumber,
  fromPhoneNumber,
}: {
  toPhoneNumber: string;
  fromPhoneNumber: string;
}) {
  return (
    <Typography variant="body2">
      <strong>{prettyPhoneNumber(toPhoneNumber)}</strong>{" "}
      <FormattedMessage
        description="Label for a thread's from phone number"
        defaultMessage="from {fromPhoneNumber}"
        values={{
          fromPhoneNumber: prettyPhoneNumber(fromPhoneNumber),
        }}
      />
    </Typography>
  );
}

function ContactName({
  assignment,
  loading,
}: {
  assignment: AssignmentSummary;
  loading: boolean;
}) {
  const intl = useIntl();
  return (
    <Typography variant="body1Bold">
      <ThreadIcon
        needsTexterResponse={assignment.needsTexterResponse}
        isOptedOut={false}
        isLoading={loading}
      />
      {formatContactName(
        {
          firstName: assignment.contactFirstName,
          lastName: assignment.contactLastName,
        },
        { formatMessage: intl.formatMessage }
      )}
    </Typography>
  );
}
